import { fDate } from 'src/utils/format-time';
import type { MetricBarType } from 'src/components/data-grid/generic-data-grid';

export const PAGE_PADDING_X = '24px';
export const CARD_HEIGHT = 80;
export const CHART_HEIGHT = 403;
export const CHART_INNER_HEIGHT = 300;
export const SECTION_GAP = 2;

// Fixed data-grid body height → internal vertical scroll (Competitors pattern).
export const GRID_HEIGHT = 600;

// Default visible metrics in the data grid, in the Figma maquette order (#10):
// Brand Searches, Blended CTR, Brand Traffic, Blended CPC, Ad Cost.
export const GRID_DEFAULT_METRICS = [
  'brand_searches',
  'blended_ctr',
  'total_brand_traffic',
  'blended_cpc',
  'ad_cost',
];

// Maquette column order (#10). Drives the grid's left-to-right column order via the
// `columnOrder` prop; metrics not listed here fall back to the KPI catalog `order`.
export const GRID_COLUMN_ORDER = [
  'brand_searches',
  'blended_ctr',
  'total_brand_traffic',
  'blended_cpc',
  'ad_cost',
];

// In-cell bar per metric (maquette #10): magnitude bar (value ÷ page-max), composition stacked
// bar for Brand Traffic, no bar for the Blended CPC ratio.
export const GRID_BAR_TYPES: Record<string, MetricBarType> = {
  brand_searches: 'magnitude',
  total_brand_traffic: 'composition',
  blended_ctr: 'magnitude',
  ad_cost: 'magnitude',
  blended_cpc: 'none',
};

// Per-metric magnitude bar color overrides (Figma). Fields omitted fall back to the KPI color.
export const GRID_BAR_COLORS: Record<string, string> = {
  brand_searches: '#EE7A63',
  ad_cost: '#DAA335',
};

// Tokens couleurs Figma (Rebranding — Signal 2026 Q2)
export const EXPLORE_COLORS = {
  borderNeutral: '#EAEAEA',
  gridBorder: 'rgba(145, 158, 171, 0.2)', // séparateurs de cellules pleins — Figma color/azure/62 @ 20%
  rowHover: 'rgba(145, 158, 171, 0.08)', // fond de ligne au survol (azure 62 @ 8%)
  headerBg: '#FBFBFC', // Figma color/azure/90 @ 14% aplati sur blanc (opaque → pas de double-teinte sur les colonnes épinglées)
  footerBg: '#FAFAFA', // fond du footer de pagination de la datagrid
  labelMuted: '#637381',
  valueDark: '#333C47',
  textDisabled: '#919EAB',
  cardSelectedBg: '#F9FAFC',
  tabActive: '#7655C1',
  countryUnderline: '#9982CC',
};

// Couleurs de séries du graphe barres+ligne (réutilise la palette Funnel)
export const CHART_COLORS = {
  bar: '#ffb700', // métrique en barres (ex. Ad Cost)
  line: '#5119b7', // métrique en ligne (ex. Brand Traffic)
};

export interface ExploreTab {
  label: string;
  value: number;
  groupby: string[];
}

/**
 * Onglets de base de la datagrid Explore. L'onglet "Countries" (value 1,
 * groupby region_name) n'est inclus que si la sélection courante porte sur
 * PLUS D'UN pays : un filtre sur un seul pays (ou "All" sur un workspace
 * mono-pays) ne donnerait qu'une ligne, donc le découpage par pays n'a pas de
 * sens (et le bandeau pays est lui aussi masqué dans ce cas).
 * Les autres `value` restent stables (0, 2, 3, + 8 plateformes) pour ne pas
 * casser les lookups groupby/tri.
 */
export const buildExploreBaseTabs = (
  timestep: string | undefined,
  selectedCountries: string[] | undefined
): ExploreTab[] => {
  const showCountriesTab = (selectedCountries?.length ?? 0) > 1;
  const dateField = (timestep ?? 'date').toLowerCase().replace('date_day', 'date');
  return [
    { label: 'dashboard.global.keywords', value: 0, groupby: ['search_term'] },
    ...(showCountriesTab
      ? [{ label: 'dashboard.global.countries', value: 1, groupby: ['region_name'] }]
      : []),
    { label: 'dashboard.global.devices', value: 2, groupby: ['device_type'] },
    { label: 'dashboard.global.date', value: 3, groupby: [dateField] },
  ];
};

export const formatExploreLabels = (
  timeSeries: any[],
  timestep: string,
  _lang?: string
): string[] =>
  (timeSeries ?? []).map((item) => {
    const dateStr =
      timestep === 'date_day'
        ? item?.date
        : item?.date ?? item?.year_week ?? item?.date_interval ?? '';
    return timestep === 'date_day' ? fDate(dateStr, 'yyyy-MM-dd') : dateStr;
  });
